import { LocationTypeDisplayId } from "../Practice/schema"

import { posthog } from "../../posthogInstance"
import { PracticeRoleEnum, UserOnboardingStepsEnum } from "../Account/userSlice"
import { ContactTypeMap } from "../CMS/schema"
import { PracticeTypeDisplayId } from "../enums/displayId.enum"
import { FeatureFlag } from "../enums/featureFlags"
import {
  initialAuthAndReleases,
  initialPracticeAgreement,
} from "../Practice/model"
import {
  PhoneEmailFax,
  Practice,
  PracticeInviteWithContext,
  PracticeServiceInviteStatusDisplayId,
  PracticeStatus,
  PracticeStatusEnum,
  selectPracticeInviteStatusDisplayId,
  PracticeInviteStatusDisplayId,
} from "../Practice/schema"
import {
  Provider,
  ProviderStatusDisplayId,
  ProviderStatusDisplayIdEnum,
} from "../Provider/schema"
import {
  ContactEmail,
  ContactFax,
  ContactPhone,
  OnboardingFlowIdEnum,
  PartialPractice,
} from "./schema"
import { hasConditionalReqs } from "./utils"

/**
 * Converts a contact value based on its type.
 * @param {ContactPhone | ContactEmail | ContactFax} contact - The contact object to convert.
 * @returns {string} The converted contact value.
 */
function convertContactValue(
  contact: ContactPhone | ContactEmail | ContactFax,
): string {
  let value: string
  // if firstEntry has fields 'phoneType' -> ContactPhone
  // else if firstEntry has field 'address' -> ContactEmail
  // else -> ContactFax
  if (contact.hasOwnProperty("phoneType")) {
    value = (contact as ContactPhone).number
  } else if (contact.hasOwnProperty("address")) {
    value = (contact as ContactEmail).address
  } else {
    value = (contact as ContactFax).number
  }
  return value
}

/**
 * Determines the contact type from a given contact object.
 * @param {ContactPhone | ContactEmail | ContactFax} contact - The contact object to analyze.
 * @returns {keyof ContactTypeMap} The determined contact type.
 */
function convertContactType(
  contact: ContactPhone | ContactEmail | ContactFax,
): keyof ContactTypeMap {
  let contactType: string
  // if firstEntry has fields 'phoneType' -> ContactPhone
  // else if firstEntry has field 'address' -> ContactEmail
  // else -> ContactFax
  if (contact.hasOwnProperty("phoneType")) {
    contactType = (contact as ContactPhone).phoneType
  } else if (contact.hasOwnProperty("address")) {
    contactType = (contact as ContactEmail).contactType
  } else {
    contactType = (contact as ContactFax).contactType
  }
  return contactType as keyof ContactTypeMap
}

/**
 * Converts an array of full practice contacts to PhoneEmailFax array.
 * @param {(ContactPhone[] | ContactEmail[] | ContactFax[])} fullPracticeContact - The array of full practice contacts.
 * @returns {PhoneEmailFax[]} The converted array of PhoneEmailFax objects.
 */
function convertContact(
  fullPracticeContact: ContactPhone[] | ContactEmail[] | ContactFax[],
): PhoneEmailFax[] {
  return fullPracticeContact.map((contact) => ({
    id: contact.id,
    value: convertContactValue(contact),
    type: convertContactType(contact),
    isPrimary: contact.isPrimary,
    error: false,
  }))
}

/**
 * Converts a PartialPractice object to a Practice object.
 * Note: This should only be used for calculating completion, not for loading the practice Redux state.
 * Some fields require backend calls to populate (that aren't used for completeness calculation, e.g., auth release signing URL).
 * @param {PartialPractice} partialPractice - The PartialPractice object to convert.
 * @returns {Practice} The converted Practice object.
 */
export function convertPartialPracticeToPractice(
  partialPractice: PartialPractice,
): Omit<
  Practice,
  "taxInformation" | "taxInfo" | "practiceAffiliates" | "practiceInvites"
> {
  const convertedAuthReleaseRequired =
    typeof partialPractice.authReleaseRequired === "boolean"
      ? partialPractice.authReleaseRequired
      : null
  const convertedPhones =
    partialPractice.contactInfo && partialPractice.contactInfo.phones.length > 0
      ? convertContact(partialPractice.contactInfo.phones)
      : []
  const primaryEmail = partialPractice.contactInfo?.emails.find(
    (email) => email.isPrimary,
  )
  const convertedPrimaryEmail = primaryEmail
    ? convertContact([primaryEmail])[0].value
    : ""
  const altEmails =
    partialPractice.contactInfo?.emails.filter((email) => !email.isPrimary) ||
    []
  const convertedAltEmails =
    altEmails.length > 0 ? convertContact(altEmails) : []
  const convertedFaxes =
    partialPractice.contactInfo && partialPractice.contactInfo.faxes.length > 0
      ? convertContact(partialPractice.contactInfo.faxes)
      : []

  const convertedPractice: Omit<
    Practice,
    "taxInformation" | "taxInfo" | "practiceAffiliates" | "practiceInvites"
  > = {
    id: partialPractice.id,
    name: partialPractice.name,
    legacyAffiliateId: partialPractice.legacyAffiliateId,
    status: partialPractice.status || "pending",
    isVisible: partialPractice.isVisible,
    websiteUrl: partialPractice.websiteUrl,
    overview: partialPractice.overview,
    logoImageUrl: partialPractice.logoImageUrl,
    deleteDateTime: undefined,
    createdDateTime: partialPractice.createdDateTime,
    createdBy: partialPractice.createdBy ?? "",
    lastUpdatedDateTime: partialPractice.lastUpdatedDateTime,
    authReleaseRequired: convertedAuthReleaseRequired,
    hasOnlineServicesInCalifornia:
      !!partialPractice.hasOnlineServicesInCalifornia,
    hasNoPhysicalLocations: partialPractice.hasNoPhysicalLocations ?? null,
    isKnoxKeeneStale: !!partialPractice.isKnoxKeeneStale,
    isPracticeAgreementStale: !!partialPractice.isPracticeAgreementStale,
    tags: partialPractice.tags,
    practiceTypeDisplayId: PracticeTypeDisplayId.STANDARD,
    practiceInfo: partialPractice.billingAddress
      ? {
          practiceAddress: {
            addressOne: partialPractice.billingAddress.line1,
            addressTwo: partialPractice.billingAddress.line2,
            city: partialPractice.billingAddress.city,
            county: partialPractice.billingAddress.county,
            stateOrTerritory: partialPractice.billingAddress.stateOrTerritory,
            zipCode: partialPractice.billingAddress.postalCode,
          },
          practiceContact: {
            phones: convertedPhones,
            primaryEmail: convertedPrimaryEmail,
            altEmails: convertedAltEmails,
            faxes: convertedFaxes,
          },
          practiceDetails: undefined, // this isnt hooked up yet for Practice or FullPractice
        }
      : {
          practiceAddress: undefined,
          practiceContact: undefined,
          practiceDetails: undefined,
        },
    providersInfo: [], // not available on partialPractice
    authAndReleases: partialPractice.authRelease
      ? {
          signedFileId: partialPractice.authRelease.executedFileId,
          signedOn: partialPractice.authRelease.executedFileId
            ? new Date(partialPractice.authRelease.lastUpdatedDateTime)
            : null,
          signedBy: partialPractice.authRelease.signedBy,
          signingUrl: "", // not available on partialPractice
        }
      : initialAuthAndReleases,
    billingAddress: partialPractice.billingAddress
      ? {
          practiceId: partialPractice.billingAddress.practiceId,
          line1: partialPractice.billingAddress.line1,
          line2: partialPractice.billingAddress.line2,
          city: partialPractice.billingAddress.city,
          stateOrTerritory: partialPractice.billingAddress.stateOrTerritory,
          postalCode: partialPractice.billingAddress.postalCode,
          county: partialPractice.billingAddress.county,
          legacyLocationId: partialPractice.billingAddress.legacyLocationId,
          createdBy: "", // not available on partialPractice
          createdDateTime: partialPractice.billingAddress.createdDateTime,
          lastUpdatedDateTime:
            partialPractice.billingAddress.lastUpdatedDateTime,
        }
      : null,
    liabilityInsurances: partialPractice.liabilityInsurances.map(
      (insurance) => ({
        id: insurance.id,
        issueDate: new Date(insurance.issueDate),
        expirationDate: new Date(insurance.expirationDate),
        insuranceStatus: insurance.insuranceStatus,
        fileId: insurance.fileId,
        fileName: insurance.fileName,
        fileStatus: insurance.fileStatus,
        createdBy: insurance.createdBy,
        createdDateTime: new Date(insurance.createdDateTime),
        lastUpdatedBy: insurance.lastUpdatedBy,
        lastUpdatedDateTime: new Date(insurance.lastUpdatedDateTime),
      }),
    ),
    locations: partialPractice.locations
      .filter(
        (location) =>
          location.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL,
      )
      .map((location) => ({
        id: location.id,
        line1: location.line1,
        line2: location.line2,
        city: location.city,
        county: "", // not available on partialPractice
        lastUpdated: null, // not available on partialPractice
        stateOrTerritory: location.stateOrTerritory,
        postalCode: location.postalCode,
        fax: location.fax,
        isPrimary: location.isPrimary,
        isAdaAccessible: location.isAdaAccessible,
        officeRequirementsCertified: location.officeRequirementsCertified,
        locationTypeDisplayId: location.locationTypeDisplayId,
        phones: location.phones,
        emails: location.emails,
      })),
    locationPhotos: [],
    virtualLocation: partialPractice.locations
      .filter(
        (location) =>
          location.locationTypeDisplayId === LocationTypeDisplayId.VIRTUAL,
      )
      .map((location) => ({
        id: location.id,
        phoneNumber: location.phones[0]?.number ?? "",
        phoneType: location.phones[0]?.locationPhoneTypeDisplayId ?? "",
        emailAddress: location.emails[0]?.address ?? "",
        faxNumber: location.fax,
        // TODO: combine practice.virtualLocation and practice.locations (physical)
        // Both should be the Location type defined above and source from the /locations endpoint
        locationTypeDisplayId:
          LocationTypeDisplayId.VIRTUAL as LocationTypeDisplayId.VIRTUAL,
        officeRequirementsCertified: location.officeRequirementsCertified,
      }))[0],
    practiceAgreement: {
      practiceAgreement: partialPractice.practiceAgreement
        ? {
            signedFileId: partialPractice.practiceAgreement.executedFileId,
            signedOn: partialPractice.practiceAgreement.executedFileId
              ? new Date(partialPractice.practiceAgreement.lastUpdatedDateTime)
              : null,
            resetOn: partialPractice.practiceAgreement.resetDateTime
              ? new Date(partialPractice.practiceAgreement.resetDateTime)
              : null,
            signedBy: partialPractice.practiceAgreement.signedBy,
            fileStatusDisplayId: null, // not available on partialPractice
          }
        : initialPracticeAgreement.practiceAgreement,
      knoxKeeneAgreement: partialPractice.knoxKeeneAgreement
        ? {
            signedFileId: partialPractice.knoxKeeneAgreement.executedFileId,
            signedOn: partialPractice.knoxKeeneAgreement.executedFileId
              ? new Date(partialPractice.knoxKeeneAgreement.lastUpdatedDateTime)
              : null,
            resetOn: partialPractice.knoxKeeneAgreement.resetDateTime
              ? new Date(partialPractice.knoxKeeneAgreement.resetDateTime)
              : null,
            signedBy: partialPractice.knoxKeeneAgreement.signedBy,
            fileStatusDisplayId: null, // not available on partialPractice
          }
        : initialPracticeAgreement.knoxKeeneAgreement,
      redirectUrl: "", // not available on partialPractice
    },
  }

  return convertedPractice
}

/**
 * IMPORTANT: THIS SHOULD ONLY BE USED in the autoSelectAffiliatedOrInvitePracticeId helper
 *
 * Selects an invite automatically based on the following heirarchy:
 *  1) First try taking the oldest accepted invite if it exists - this corresponds to the first invite that the user has actually begun onboarding with
 *  2) If no accepted invite exists, then use the linked invite if it exists - this is the invite that the user was linked into the app with
 *  3) If no linked invite, then the oldest invite with 'Pending' status
 *  4) Else the user has not been linked to any invites so we return undefined
 *
 * @param {PracticeInviteWithContext[]} practiceInvites - An array of practice invites to select from.
 * @returns {PracticeInviteWithContext | undefined} The selected invite or undefined if no invite is selected.
 */
const getAutoSelectedInvite = (
  practiceInvites: PracticeInviteWithContext[],
  linkedInviteId?: string,
): PracticeInviteWithContext | undefined => {
  let selectedInvite: PracticeInviteWithContext | undefined

  const acceptedInvites = practiceInvites.filter(
    (invite) =>
      invite.inviteStatusDisplayId ===
      PracticeServiceInviteStatusDisplayId.ACCEPTED,
  )
  const linkedInvite = practiceInvites.find(
    (invite) => invite.id === linkedInviteId,
  )
  const pendingInvites = practiceInvites.filter(
    (invite) =>
      selectPracticeInviteStatusDisplayId(invite.inviteStatusDisplayId) ===
        PracticeInviteStatusDisplayId.SENT && !!invite.userLinkedOn,
  )

  if (acceptedInvites.length > 0) {
    selectedInvite = acceptedInvites.reduce(
      (oldest, current) =>
        new Date(oldest.userAcceptedOn!) < new Date(current.userAcceptedOn!)
          ? oldest
          : current,
      acceptedInvites[0],
    )
  }

  if (!selectedInvite && !!linkedInvite) {
    selectedInvite = linkedInvite
  }

  if (!selectedInvite && pendingInvites.length > 0) {
    selectedInvite = pendingInvites.reduce(
      (oldest, current) =>
        new Date(oldest.userLinkedOn!) < new Date(current.userLinkedOn!)
          ? oldest
          : current,
      pendingInvites[0],
    )
  }

  return selectedInvite
}

export enum AutoSelectedPracticeIdEnum {
  INVITE = "invite",
  AFFILIATED = "affiliated",
}
export type AutoSelectedPracticeIdResult = {
  type: AutoSelectedPracticeIdEnum
  practiceId: string
  associatedInviteId?: string
}
/**
 * Determines the auto-selected practice ID for onboarding based on the user's practice invites and existing practice associations.
 *
 * 1) if the user has both a self created practice and an (accepted) invite, then take the eldest of the two
 *       (practice created date versus invite accepted date time)
 * 2) if only a self created practice exists then use that
 *    we auto select the oldest so that we know the user gets the same auto selection each time
 * 3) if only an invite exists then use that
 * 4) else return undefined
 *
 * @param {PartialPractice[]} partialPractices - An array of practice invites for the user.
 * @param {PracticeName[]} practiceNames - An array of practices where the user already has a role.
 * @returns {string} The auto-selected practice ID. Returns an empty string if no practice is auto-selected.
 *
 * @description
 * This function auto selects a practice only if there is a single associated practice to the given user.
 * Otherwise we have the user manually select a practice in the practice profile screen.
 */
export const autoSelectAffiliatedOrInvitePracticeId = (
  partialPractices: PartialPractice[], // these are the practices where the user ALREADY has a role
  practiceInvites: PracticeInviteWithContext[],
  linkedInviteId?: string, // this is the id of the invite that the user entered the app with
): AutoSelectedPracticeIdResult | undefined => {
  let autoSelectedAffiliatedOrInvitePracticeId:
    | AutoSelectedPracticeIdResult
    | undefined
  const selectedInvite = getAutoSelectedInvite(practiceInvites, linkedInviteId)

  const invitePracticeIds = practiceInvites.map((invite) => invite.practiceId)
  // user is affiliated to the selected practice if the practice id corresponds to one of the partial practices
  const affiliateCreatedForInvite =
    selectedInvite?.practiceId &&
    partialPractices
      .map((practice) => practice.id)
      .includes(selectedInvite.practiceId)

  const nonInvitePractices = partialPractices.filter(
    (practice) => !invitePracticeIds.includes(practice.id),
  )
  const selectedPractice = nonInvitePractices.reduce(
    (oldest, current) =>
      new Date(oldest.createdDateTime) < new Date(current.createdDateTime)
        ? oldest
        : current,
    nonInvitePractices[0], // Initial value
  )
  if (!!selectedInvite && !!selectedPractice) {
    // take the practice id from the oldest of the practice and (accepted) invite
    const inviteAcceptancePredatesPractice =
      selectedInvite.inviteStatusDisplayId ===
        PracticeServiceInviteStatusDisplayId.ACCEPTED &&
      selectedInvite?.userAcceptedOn &&
      new Date(selectedInvite?.userAcceptedOn) <=
        new Date(selectedPractice?.createdDateTime)
    autoSelectedAffiliatedOrInvitePracticeId = inviteAcceptancePredatesPractice
      ? {
          practiceId: selectedInvite.practiceId,
          type: affiliateCreatedForInvite
            ? AutoSelectedPracticeIdEnum.AFFILIATED
            : AutoSelectedPracticeIdEnum.INVITE,
          associatedInviteId: selectedInvite.id,
        }
      : {
          practiceId: selectedPractice.id,
          type: AutoSelectedPracticeIdEnum.AFFILIATED,
        }
  } else if (!!selectedPractice) {
    autoSelectedAffiliatedOrInvitePracticeId = {
      practiceId: selectedPractice.id,
      type: AutoSelectedPracticeIdEnum.AFFILIATED,
    }
  } else if (!!selectedInvite) {
    autoSelectedAffiliatedOrInvitePracticeId = {
      practiceId: selectedInvite.practiceId,
      type: affiliateCreatedForInvite
        ? AutoSelectedPracticeIdEnum.AFFILIATED
        : AutoSelectedPracticeIdEnum.INVITE,
      associatedInviteId: selectedInvite.id,
    }
  }

  return autoSelectedAffiliatedOrInvitePracticeId
}

export const userIsProviderSeeingPatients = (
  provider: Provider,
  autoSelectedPracticeIdResult?: AutoSelectedPracticeIdResult,
  practiceInvites?: PracticeInviteWithContext[],
) => {
  let isSeeingPatients = false
  const hasAssociatedInvite = !!autoSelectedPracticeIdResult?.associatedInviteId
  if (!hasAssociatedInvite) {
    isSeeingPatients = !!provider.id
  } else {
    const selectedInvite = practiceInvites?.find(
      (invite) =>
        invite.id === autoSelectedPracticeIdResult?.associatedInviteId,
    )
    isSeeingPatients = !!selectedInvite?.inviteRoleDisplayIds.includes(
      PracticeRoleEnum.PROVIDER,
    )
  }

  return isSeeingPatients
}

interface GetOnboardingFlowIdFromStateProps {
  partialPractices: PartialPractice[]
  provider: Provider | null
  detachedProviders: Provider[]
  practiceInvites: PracticeInviteWithContext[]
  autoSelectedPracticeIdResult: AutoSelectedPracticeIdResult | undefined
  isSeeingPatients?: boolean
}
// Calculates the onboarding flow id based off current state
// NOTE: If the user is not onboarded then we need to find the associated invite for the
//       current practice to calculate the flow id. If no such invite exists then we can
//       assume we have a new user who is creating a practice or practice/provider.
//       Otherwise we are in some faulty state.
export const getOnboardingFlowIdFromState = ({
  partialPractices,
  provider,
  detachedProviders,
  practiceInvites,
  autoSelectedPracticeIdResult,
  isSeeingPatients = false,
}: GetOnboardingFlowIdFromStateProps): OnboardingFlowIdEnum => {
  const practiceId = autoSelectedPracticeIdResult?.practiceId
  // Note: our auto selectPracticeId either be on one of the affiliated practices (partialPractices),
  //       on an invite (practiceInvites), or be undefined
  const affiliatedPractice = partialPractices.find(
    (practice) => practice.id === practiceId,
  )
  const selectedInvite = practiceInvites.find(
    (invite) => invite.practiceId === practiceId,
  )
  const detachedProvider = detachedProviders.find(
    (provider) => provider.id === selectedInvite?.providerId,
  )

  // existence checks
  const providerExists = !!provider?.id
  const currentInviteExists = !!selectedInvite

  // practice role based selection
  // note: affiliate must at least be a manager if not associated to an invite
  const includePracticeManager = currentInviteExists
    ? selectedInvite?.inviteRoleDisplayIds.includes(
        PracticeRoleEnum.PRACTICE_MANAGER,
      )
    : true
  const includeProvider =
    isSeeingPatients ||
    (currentInviteExists
      ? selectedInvite?.inviteRoleDisplayIds.includes(PracticeRoleEnum.PROVIDER)
      : providerExists)
  const includeAdmin = selectedInvite?.inviteRoleDisplayIds.includes(
    PracticeRoleEnum.PRACTICE_ADMIN,
  )
  const includeBilling = selectedInvite?.inviteRoleDisplayIds.includes(
    PracticeRoleEnum.PRACTICE_BILLING,
  )

  // existence of conditional reqs
  // if we have a current practice then we can check the practice directly
  // invited users need to check the invite for cond reqs
  const affiliatedPracticeHasCondReqs = currentInviteExists
    ? selectedInvite.practiceHasConditionalReqs
    : hasConditionalReqs(affiliatedPractice)

  // check for migration
  // NOTE: migration is only possible via invites, so we can check the invite directly
  const isMigrationInvite =
    currentInviteExists &&
    selectedInvite.createdBy === window._env_.MIGRATION_SERVICE_USER_ID
  const providerToCheck = provider?.id ? provider : detachedProvider

  // specific bools for each flow - this ensures that we have an error state if none of the conditions are met
  // and helps a little with readability by avoiding triple nested conditionals
  const noInvitesManagerOnly =
    !currentInviteExists && includePracticeManager && !includeProvider
  const noInvitesManagerAndProviderNoCondReqs =
    !currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    !affiliatedPracticeHasCondReqs
  const noInvitesManagerAndProviderWithCondReqs =
    !currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    affiliatedPracticeHasCondReqs
  const invitedManagerOnlyMigratedAndAutoActivated =
    currentInviteExists &&
    includePracticeManager &&
    !includeProvider &&
    isMigrationInvite &&
    practiceIsActive(selectedInvite.practiceStatus)
  const invitedManageAndProviderAndAutoActivated =
    currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    isMigrationInvite &&
    practiceIsActive(selectedInvite.practiceStatus) &&
    providerIsActive(providerToCheck?.providerStatusDisplayId)
  const invitedProviderOnlyAndAutoActivated =
    currentInviteExists &&
    !includePracticeManager &&
    includeProvider &&
    isMigrationInvite &&
    providerIsActive(providerToCheck?.providerStatusDisplayId)
  const invitedManagerOnly =
    currentInviteExists &&
    includePracticeManager &&
    !includeProvider &&
    !isMigrationInvite
  const invitedManagerOnlyMigrated =
    currentInviteExists &&
    includePracticeManager &&
    !includeProvider &&
    isMigrationInvite
  const invitedManagerAndProviderNoCondReq =
    currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    !affiliatedPracticeHasCondReqs &&
    !isMigrationInvite
  const invitedManagerAndProviderNoCondReqMigrated =
    currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    !affiliatedPracticeHasCondReqs &&
    isMigrationInvite
  const invitedManagerAndProviderWithCondReq =
    currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    affiliatedPracticeHasCondReqs &&
    !isMigrationInvite
  const invitedManagerAndProviderWithCondReqMigrated =
    currentInviteExists &&
    includePracticeManager &&
    includeProvider &&
    affiliatedPracticeHasCondReqs &&
    isMigrationInvite
  const invitedProviderOnlyNoCondReq =
    currentInviteExists &&
    !includePracticeManager &&
    includeProvider &&
    !affiliatedPracticeHasCondReqs &&
    !isMigrationInvite
  const invitedProviderOnlyNoCondReqMigrated =
    currentInviteExists &&
    !includePracticeManager &&
    includeProvider &&
    !affiliatedPracticeHasCondReqs &&
    isMigrationInvite
  const invitedProviderOnlyWithCondReq =
    currentInviteExists &&
    !includePracticeManager &&
    includeProvider &&
    affiliatedPracticeHasCondReqs &&
    !isMigrationInvite
  const invitedProviderOnlyWithCondReqMigrated =
    currentInviteExists &&
    !includePracticeManager &&
    includeProvider &&
    affiliatedPracticeHasCondReqs &&
    isMigrationInvite
  const invitedAdmin = currentInviteExists && includeAdmin
  const invitedBilling = currentInviteExists && includeBilling

  switch (true) {
    case noInvitesManagerOnly:
      return OnboardingFlowIdEnum.NO_INVITES_MANAGER
    case noInvitesManagerAndProviderNoCondReqs:
      return OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_NO_COND_REQ
    case noInvitesManagerAndProviderWithCondReqs:
      return OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_WITH_COND_REQ
    case invitedManagerOnlyMigratedAndAutoActivated:
      return OnboardingFlowIdEnum.INVITED_MANAGER_MIGRATED_AUTO_ACTIVATED
    case invitedManageAndProviderAndAutoActivated:
      return OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_MIGRATED_AUTO_ACTIVATED
    case invitedProviderOnlyAndAutoActivated:
      return OnboardingFlowIdEnum.INVITED_PROVIDER_MIGRATED_AUTO_ACTIVATED
    case invitedManagerOnly:
      return OnboardingFlowIdEnum.INVITED_MANAGER
    case invitedManagerOnlyMigrated:
      return OnboardingFlowIdEnum.INVITED_MANAGER_MIGRATED
    case invitedManagerAndProviderNoCondReq:
      return OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ
    case invitedManagerAndProviderNoCondReqMigrated:
      return OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ_MIGRATED
    case invitedManagerAndProviderWithCondReq:
      return OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ
    case invitedManagerAndProviderWithCondReqMigrated:
      return OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ_MIGRATED
    case invitedProviderOnlyNoCondReq:
      return OnboardingFlowIdEnum.INVITED_PROVIDER_NO_COND_REQ
    case invitedProviderOnlyNoCondReqMigrated:
      return OnboardingFlowIdEnum.INVITED_PROVIDER_NO_COND_REQ_MIGRATED
    case invitedProviderOnlyWithCondReq:
      return OnboardingFlowIdEnum.INVITED_PROVIDER_WITH_COND_REQ
    case invitedProviderOnlyWithCondReqMigrated:
      return OnboardingFlowIdEnum.INVITED_PROVIDER_WITH_COND_REQ_MIGRATED
    case invitedAdmin:
      return OnboardingFlowIdEnum.INVITED_ADMIN
    case invitedBilling:
      return OnboardingFlowIdEnum.INVITED_BILLING
    default:
      return OnboardingFlowIdEnum.ERROR
  }
}

export const isUserOnboarded = (
  flowId: OnboardingFlowIdEnum,
  completedUserOnboardingSteps: UserOnboardingStepsEnum[],
  affiliatedPractice?: PartialPractice,
  provider?: Provider | null,
  isHAUser?: boolean,
): boolean => {
  if (isHAUser) {
    return true
  }

  if (
    posthog.isFeatureEnabled(FeatureFlag.IsProviderOrphanSupport16529Enabled) &&
    provider?.providerStatusDisplayId === ProviderStatusDisplayIdEnum.ORPHANED
  ) {
    return true
  }

  switch (flowId) {
    case OnboardingFlowIdEnum.NO_INVITES_MANAGER:
      return (
        practiceIsActive(affiliatedPractice?.status) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_NO_COND_REQ:
      return (
        practiceIsActive(affiliatedPractice?.status) &&
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.NO_INVITES_MANAGER_AND_PROVIDER_WITH_COND_REQ:
      return (
        practiceIsActive(affiliatedPractice?.status) &&
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER_MIGRATED_AUTO_ACTIVATED:
    case OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_MIGRATED_AUTO_ACTIVATED:
    case OnboardingFlowIdEnum.INVITED_PROVIDER_MIGRATED_AUTO_ACTIVATED:
      return hasCompeletedFinalOnboardingStep(
        completedUserOnboardingSteps,
        UserOnboardingStepsEnum.GETTING_STARTED,
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER:
      return hasCompeletedFinalOnboardingStep(
        completedUserOnboardingSteps,
        UserOnboardingStepsEnum.GETTING_STARTED,
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER_MIGRATED:
      return (
        practiceIsActive(affiliatedPractice?.status) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ:
      return (
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_NO_COND_REQ_MIGRATED:
      return (
        practiceIsActive(affiliatedPractice?.status) &&
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ:
      return (
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_MANAGER_AND_PROVIDER_WITH_COND_REQ_MIGRATED:
      return (
        practiceIsActive(affiliatedPractice?.status) &&
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_PROVIDER_NO_COND_REQ:
      return (
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_PROVIDER_NO_COND_REQ_MIGRATED:
      return (
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_PROVIDER_WITH_COND_REQ:
      return (
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_PROVIDER_WITH_COND_REQ_MIGRATED:
      return (
        providerIsActive(provider?.providerStatusDisplayId) &&
        hasCompeletedFinalOnboardingStep(
          completedUserOnboardingSteps,
          UserOnboardingStepsEnum.ACTIVATION,
        )
      )
    case OnboardingFlowIdEnum.INVITED_ADMIN:
      return hasCompeletedFinalOnboardingStep(
        completedUserOnboardingSteps,
        UserOnboardingStepsEnum.GETTING_STARTED,
      )
    case OnboardingFlowIdEnum.INVITED_BILLING:
      return hasCompeletedFinalOnboardingStep(
        completedUserOnboardingSteps,
        UserOnboardingStepsEnum.GETTING_STARTED,
      )
    default:
      return false
  }
}

const practiceIsActive = (
  practiceStatus?: PracticeStatusEnum | PracticeStatus,
): boolean => {
  return (
    !!practiceStatus &&
    [
      PracticeStatusEnum.ACTIVE,
      PracticeStatusEnum.DEACTIVATED,
      PracticeStatusEnum.SUSPENDED,
    ].includes(practiceStatus as PracticeStatusEnum)
  )
}

const providerIsActive = (
  providerStatus?: ProviderStatusDisplayIdEnum | ProviderStatusDisplayId,
): boolean => {
  return (
    !!providerStatus &&
    [
      ProviderStatusDisplayIdEnum.ACTIVE,
      ProviderStatusDisplayIdEnum.DEACTIVATED,
      ProviderStatusDisplayIdEnum.SUSPENDED,
    ].includes(providerStatus as ProviderStatusDisplayIdEnum)
  )
}

const hasCompeletedFinalOnboardingStep = (
  completedUserOnboardingSteps: UserOnboardingStepsEnum[],
  finalStep: UserOnboardingStepsEnum,
): boolean => {
  return completedUserOnboardingSteps.includes(finalStep)
}
